import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import numeri from 'numeri';
import _ from 'lodash';
import s from './rivers.css';

const leftPadding = 40;
const bottomPadding = 40;
const padding = 20;
const width = 700;
const height = 600;
const w0 = leftPadding;
const w1 = width - padding;
const hH0 = padding;
const hH1 = (height - bottomPadding) / 2;
const vH0 = padding + (height - bottomPadding) / 2;
const vH1 = height - bottomPadding;
const n = 10;
const nPts = 401;
const svgId = 'rivers-svg';
const nodeR = 6;
const bigNodeR = 10;

const g = 9.8; // m / s^2
const phi = 70; // m^3 / s
const w = 15; // m
const xMax = 1000; // m
const yMax = 20; // m
const alpha = 0.01; // unitless

const minXSpacing = 50;
const criticalH = (phi * phi / w / w / g) ** (1 / 3);
const criticalV = phi / criticalH / w;
const ptDx = xMax / (nPts - 1);
const criticalC = (g * w * criticalH ** 4) / 4 - phi * phi * w / criticalH;

const coordRound = (numeriArr, scale) => {
  return numeriArr.map((c) => scale(c));
};

const derivNumer = (h, slope) => {
  return g * w ** 3 * h ** 3 * slope + alpha * phi ** 2 * (w + 2 * h);
};

const deriv = (h, slope) => {
  const numer = derivNumer(h, slope);
  const denom = g * w ** 3 * h ** 3 - phi ** 2 * w;
  return {
    hprime: -numer / denom,
    isSc: denom < 0,
  };
};

const scImplicitFn = (h) => {
  return g * w ** 3 * h ** 4 / 4 - phi ** 2 * w * h;
};

const scTransition = (h, hprime, nextSlope, goSc) => {
  const intercept = (criticalH - h) / hprime;
  const criticalNumer = derivNumer(criticalH, nextSlope);
  if (criticalNumer >= 0) {
    // invalid river
    return {
      h: criticalH,
      critical: true,
      valid: false,
    };
  }
  const remainingDx = ptDx - intercept;
  let hMin;
  let hMax;
  if (goSc) {
    // sub->supercritical transition, take the smaller root
    hMin = criticalH * 0.8;
    hMax = criticalH;
  } else {
    // super->subcritical transition, take the bigger root
    hMin = criticalH;
    hMax = criticalH * 2;
  }

  while (hMax - hMin > criticalH / 1000) {
    const hMid = (hMin + hMax) / 2;
    if (
      derivNumer(hMid, nextSlope) < 0 &&
      (scImplicitFn(hMid) < criticalC - criticalNumer * remainingDx) === goSc
    ) {
      hMax = hMid;
    } else {
      hMin = hMid;
    }
  }

  return {
    h: (hMin + hMax) / 2,
    critical: true,
    valid: true,
  };
};

const gradAdd = (h, dh) => {
  return Math.min(Math.max(h + dh, h * 0.4), h * 2);
};

const criticalRk4Iter = (h, slope, nextSlope, goSc) => {
  const { hprime: a, isSc: startSc } = deriv(h, slope);
  const { hprime: b, isSc: scB } = deriv(gradAdd(h, a * ptDx / 2), slope);
  if (scB !== startSc) {
    return scTransition(h, a, nextSlope, goSc);
  }
  const { hprime: c, isSc: scC } = deriv(gradAdd(h, b * ptDx / 2), slope);
  if (scC !== startSc) {
    return scTransition(h, b, nextSlope, goSc);
  }
  const { hprime: d, isSc: scD } = deriv(gradAdd(h, c * ptDx), slope);
  if (scD !== startSc) {
    return scTransition(h, c, nextSlope, goSc);
  }
  const hprime = (a + 2 * b + 2 * c + d) / 6;
  const res = gradAdd(h, hprime * ptDx);
  if ((res < criticalH) !== startSc) {
    return scTransition(h, hprime, nextSlope, goSc);
  }
  return {
    h: res,
    critical: false,
    valid: true,
  };
};

const criticalRk4 = (h0, slopes) => {
  const hs = [h0];
  const criticalXs = [];
  let h = h0;
  let goSc = h0 > criticalH;
  for (let i = 0; i < nPts - 1; i++) {
    const slope = slopes[i];
    const nextSlope = slopes[i + 1] === undefined ? slopes[i] : slopes[i + 1];
    const { h: newH, critical, valid } = criticalRk4Iter(h, slope, nextSlope, goSc);
    h = newH;
    if (critical) {
      goSc = !goSc;
      criticalXs.push((i + 1) * ptDx);
    }
    hs.push(h);
    if (!valid) {
      break;
    }
  }
  return {
    hs,
    criticalXs,
  };
};

class Rivers extends React.Component {
  static propTypes = {
    initSpeed: PropTypes.number,
  }

  componentDidMount() {
    if (!this.svg) {
      this.svg = d3.select(`#${svgId}`);
    }

    this.initialize();
  }

  componentWillReceiveProps(props) {
    if ('svg' in this) {
      this.drawPts(props.initSpeed);
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  initialize() {
    this.xs = numeri.range(n + 1).times(xMax / n);
    this.ys = numeri.fill([n + 1], n).minus(numeri.range(n + 1)).times(yMax / n);
    this.xScale = d3.scaleLinear()
      .domain([0, xMax])
      .range([w0, w1]);
    this.hYScale = d3.scaleLinear()
      .domain([0, yMax + 5])
      .range([hH1, hH0]);
    this.xAxis = d3.axisBottom()
      .scale(this.xScale)
      .ticks(6)
      .tickFormat((d) => `${d / 1000}`);
    this.hYAxis = d3.axisLeft()
      .scale(this.hYScale)
      .ticks(6);
    this.svg
      .append('g')
      .attr('transform', `translate(0, ${vH1})`)
      .call(this.xAxis);
    this.svg
      .append('g')
      .attr('transform', `translate(${this.xScale(0)}, 0)`)
      .call(this.hYAxis);

    this.drawNodes();
    this.drawPts();
  }

  drawPts(v0) {
    const riverbedCoords = this.nodeXy();
    const lineGenerator = d3.line();
    this.svg.select('#riverbed').attr('d', lineGenerator(riverbedCoords));

    const nodeDxs = this.xs.slice([1, n + 1]).minus(this.xs.slice([0, n]));
    const nodeSlopes = this.ys.slice([1, n + 1])
      .minus(this.ys.slice([0, n]))
      .div(nodeDxs)
      .toNested();
    const xPts = Array(nPts);
    const yPts = Array(nPts);
    const slopes = Array(nPts);
    let j = 0;
    for (let i = 0; i < nPts; i++) {
      const x = i * xMax / (nPts - 1);
      xPts[i] = x;
      if (j < n - 1 && x > this.xs.get(j + 1)) {
        j++;
      }
      const prop = (x - this.xs.get(j)) / nodeDxs.get(j);
      yPts[i] = (1 - prop) * this.ys.get(j) + prop * this.ys.get(j + 1);

      const slopeProp = ((x + ptDx) - this.xs.get(j + 1)) / ptDx;
      if (j < n - 1 && slopeProp > 0) {
        slopes[i] = slopeProp * nodeSlopes[j + 1] + (1 - slopeProp) * nodeSlopes[j];
      } else {
        slopes[i] = nodeSlopes[j];
      }
    }

    const h0 = phi / w / (v0 || this.props.initSpeed);
    const { hs, criticalXs } = criticalRk4(
      h0,
      slopes,
      [],
    );
    const nValidPts = hs.length;
    const xCoords = coordRound(xPts.slice(0, nValidPts), this.xScale);
    // const hs = Array(nPts).fill(5);
    const asPlusHs = numeri.vector(yPts).slice([0, nValidPts]).plus(numeri.vector(hs)).toNested();
    const waterCoords = coordRound(asPlusHs, this.hYScale);
    const validRiverbedCoords = _.zip(
      xCoords,
      coordRound(yPts.slice(0, nValidPts), this.hYScale),
    );

    const vs = hs.map((h) => phi / h / w);
    const vMax = vs.reduce((a, b) => Math.max(a, b), 5.0);
    const vYScale = d3.scaleLinear()
      .domain([0, vMax])
      .range([vH1, vH0]);
    const vCoords = coordRound(vs, vYScale);
    const vYAxis = d3.axisLeft()
      .scale(vYScale)
      .ticks(6);
    if (this.vAxisG) {
      this.vAxisG.remove();
      this.criticalLines.remove();
    }
    this.vAxisG = this.svg
      .append('g')
      .attr('transform', `translate(${this.xScale(0)}, 0)`)
      .call(vYAxis);
    this.criticalLines = this.svg
      .append('g')
      .attr('id', 'critical-lines');
    this.criticalLines.selectAll('line')
      .data(criticalXs)
      .enter()
      .append('line')
      .attr('x1', (d) => this.xScale(d))
      .attr('x2', (d) => this.xScale(d))
      .attr('y1', hH0)
      .attr('y2', hH1)
      .attr('class', s.criticalline);

    const waterLevelCoords = _.zip(xCoords, waterCoords);
    const waterBodyCoords = waterLevelCoords.concat(_.reverse(validRiverbedCoords));
    this.svg.select('#waterlevel').attr('d', lineGenerator(waterLevelCoords));
    this.svg.select('#waterbody').attr('d', lineGenerator(waterBodyCoords));
    this.svg.select('#speed').attr('d', lineGenerator(_.zip(xCoords, vCoords)));
    this.svg.select('#criticality')
      .attr('transform', `translate(${0},${vYScale(criticalV)})`);
  }

  nodeId(j) {
    return `node-${j}`;
  }

  nodeXy() {
    return _.zip(
      coordRound(this.xs.toNested(), this.xScale),
      coordRound(this.ys.toNested(), this.hYScale),
    );
  }

  drawNodes() {
    const data = this.nodeXy().slice(1);
    const dragHandler = d3.drag()
      .on('drag', (d, idx) => {
        const j = idx + 1;
        const { event } = d3;
        const node = d3.select(`#${this.nodeId(j)}`);

        let y = this.hYScale.invert(event.y);
        y = Math.max(y, 0);
        y = Math.min(y, yMax);
        this.ys.set([j], y);

        if (idx < n - 1) {
          // x coord of final node is not adjustable
          let x = this.xScale.invert(event.x);
          x = Math.max(x, 0);
          x = Math.max(x, this.xs.get(j - 1) + minXSpacing);
          x = Math.min(x, xMax);
          if (j < n) {
            x = Math.min(x, this.xs.get(j + 1) - minXSpacing);
          }
          this.xs.set([j], x);
          node
            .attr('cx', this.xScale(x))
            .attr('cy', this.hYScale(y));
        } else {
          node
            .attr('cy', this.hYScale(y));
        }

        this.drawPts();
      });
    this.svg.selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', (d) => d[0])
      .attr('cy', (d) => d[1])
      .attr('class', s.node)
      .attr('id', (d, idx) => this.nodeId(idx + 1))
      .attr('r', nodeR)
      .on('mouseover', (d, idx) => {
        d3.select(`#${this.nodeId(idx + 1)}`)
          .attr('r', bigNodeR);
      })
      .on('mouseout', (d, idx) => {
        d3.select(`#${this.nodeId(idx + 1)}`)
          .attr('r', nodeR);
      });
    dragHandler(this.svg.selectAll('circle'));
  }

  render() {
    return (
      <div>
        <svg width={width} height={height} id={svgId}>
          <path id="riverbed" className={s.riverbed} />
          <path id="waterlevel" className={s.waterlevel} />
          <path id="waterbody" className={s.waterbody} />
          <path id="speed" className={s.speed} />
          <g id="criticality">
            <line className={s.criticality} x1={leftPadding} x2={width - 50} />
            <text className={s.criticalitytext} x={width} textAnchor="end" dominantBaseline="middle">
              critical
            </text>
          </g>
          <text textAnchor="middle" x={width / 2} y={height - 5}>
            distance along river / km
          </text>
          <text textAnchor="middle" transform="translate(12, 150) rotate(-90)">
            altitude / m
          </text>
          <text textAnchor="middle" transform="translate(12, 435) rotate(-90)">
            speed / (m/s)
          </text>
        </svg>
      </div>
    );
  }
}

export default withStyles(s)(Rivers);



// WEBPACK FOOTER //
// src/components/Rivers/RiversGraph.js